import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Form,
  Loader,
  Row,
  Col,
  Slider,
  Button,
  Modal,
  useModal,
} from '@lucid/lego';
import { useLocaleNavigate } from '../../../hooks/useLocaleNavigate';
import { useStore } from '../../../store/store';
import { createCaseFormFields, techSupportFormFields, escalationFormFields } from './formFields';
import { useCreateCustomerCase } from '../../../api/services/lupp/customer/customer';
import styles from './CreateCase.module.scss';
import { formatCaseTitle } from '../CaseTracker/Table/utils';
import VinSearch from '../../../components/VinSearch/VinSearch';
import type { VehicleData } from '../../../components/VinSearch/types';

const REDIRECT_DELAY = 5000;
const SUCCESS_MESSAGE = 'Case created successfully!';
const ERROR_MESSAGE = 'Failed to create case. Please try again.';
const VALIDATION_ERROR = 'Please fix the form errors before submitting.';
const REDIRECT_PATH = '/customer-care/case-tracker';

type CaseTypeCard = {
  id: string;
  title: string;
  description: string;
  caseParentCategory: string;
  caseSubCategory: string;
};

type CaseTypeSection = {
  sectionTitle: string;
  cards: CaseTypeCard[];
};

const caseTypesData: CaseTypeSection[] = [
  {
    sectionTitle: 'General Support',
    cards: [
      {
        id: 'back-office-support',
        title: 'Back Office Support',
        description:
          'Use this case type for inquires unrelated to technical support (e.g., queries that do not require vehicle physical inspection).',
        caseParentCategory: 'GENERAL_SUPPORT',
        caseSubCategory: 'BACK_OFFICE_SUPPORT',
      },
      {
        id: 'product-knowledge',
        title: 'Product Knowledge',
        description: 'Vehicle knowledge related questions or general product clarifications.',
        caseParentCategory: 'GENERAL_SUPPORT',
        caseSubCategory: 'PRODUCT_KNOWLEDGE',
      },
      {
        id: 'others',
        title: 'Others',
        description: 'Any other support inquiries not covered under the above categories.',
        caseParentCategory: 'GENERAL_SUPPORT',
        caseSubCategory: 'OTHERS',
      },
    ],
  },
  {
    sectionTitle: 'VIN Related Cases',
    cards: [
      {
        id: 'tech-support-non-service',
        title: 'Tech Support  - Non Service',
        description:
          'Use this case type for inquiries related to a vin but unrelated to technical support.',
        caseParentCategory: 'VIN_RELATED',
        caseSubCategory: 'TECH_SUPPORT_NON_SERVICE',
      },
      {
        id: 'escalation',
        title: 'Escalation',
        description: 'Enquiries that request immediately support such as legal, complain.',
        caseParentCategory: 'VIN_RELATED',
        caseSubCategory: 'ESCALATION',
      },
    ],
  },
];

type VinSearchMode = 'tech-support-non-service' | 'escalation' | null;

const CreateCase: React.FC = () => {
  const theme = useStore((state) => state.theme);
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';
  const navigate = useLocaleNavigate();
  const { mutate, isPending } = useCreateCustomerCase();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [selectedCard, setSelectedCard] = useState<CaseTypeCard | null>(null);
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const sliderContentRef = useRef<HTMLDivElement>(null);

  // VIN Search mode state
  const [vinSearchMode, setVinSearchMode] = useState<VinSearchMode>(null);
  const [vinSearchErrorMessage, setVinSearchErrorMessage] = useState<string | null>(null);
  const [vinSearchSuccessMessage, setVinSearchSuccessMessage] = useState<string>('');

  // Close confirmation modal
  const confirmModal = useModal();
  const [pendingClose, setPendingClose] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const scrollSliderToTop = () => {
    setTimeout(() => {
      if (sliderContentRef.current) {
        sliderContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleBackToSelection = () => {
    setVinSearchMode(null);
    setVinSearchErrorMessage(null);
    setVinSearchSuccessMessage('');
  };

  const handleCardClick = (card: CaseTypeCard) => {
    // VIN Related cases show VIN search component
    if (card.id === 'tech-support-non-service' || card.id === 'escalation') {
      setVinSearchMode(card.id as VinSearchMode);
      return;
    }

    setSelectedCard(card);
    setIsSliderOpen(true);
    setErrorMessage(null);
    setSuccessMessage('');
  };

  // Handle VIN search form submission
  const handleVinSearchFormSubmit = useCallback(
    (formData: unknown, selectedAsset: VehicleData) => {
      setVinSearchErrorMessage(null);
      const data = formData as Record<string, unknown>;

      const payload = {
        caseParentCategory: 'VIN_RELATED',
        caseSubCategory: vinSearchMode === 'escalation' ? 'ESCALATION' : 'TECH_SUPPORT_NON_SERVICE',
        priority: (data.priority as string) || '',
        caseSubject: (data.caseSubject as string) || '',
        caseSummary: (data.caseSummary as string) || '',
        requestedSupport: (data.requestedSupport as string) || '',
        vin: (data.vin as string) || selectedAsset.details.vin || '',
        locationOfEvent: (data.locationOfEvent as string) || '',
        stepsTaken: (data.stepsTaken as string) || '',
        attachMedia: (data.attachMedia as string[]) || [],
        status: '',
        customerName: (data.customerName as string) || selectedAsset.customer.name || '',
        customerEmail: (data.customerEmail as string) || selectedAsset.customer.email || '',
        customerPhone: (data.customerPhone as string) || selectedAsset.customer.phone || '',
      };

      mutate(payload, {
        onSuccess: (response) => {
          // response is already the data object due to LuppApi.extractData
          const caseIdentifier = response?.customerCaseIdentifier;
          const caseIdMessage = formatCaseTitle(caseIdentifier || '-');
          setVinSearchSuccessMessage(`${SUCCESS_MESSAGE} Case No. - ${caseIdMessage}`);
          setTimeout(() => {
            navigate(REDIRECT_PATH);
          }, REDIRECT_DELAY);
        },
        onError: (error: unknown) => {
          setVinSearchSuccessMessage('');
          const errorMsg = error instanceof Error ? error.message : ERROR_MESSAGE;
          setVinSearchErrorMessage(errorMsg);
        },
      });
    },
    [vinSearchMode, mutate, navigate]
  );

  const handleVinSearchFormError = useCallback((_error: unknown) => {
    setVinSearchErrorMessage(VALIDATION_ERROR);
    setVinSearchSuccessMessage('');
  }, []);

  const handleVinSearchSliderClose = useCallback(() => {
    setVinSearchErrorMessage(null);
    setVinSearchSuccessMessage('');
  }, []);

  const handleSliderClose = () => {
    if (isFormDirty) {
      setPendingClose(true);
      confirmModal.openModal();
    } else {
      setIsSliderOpen(false);
      setSelectedCard(null);
    }
  };

  const handleConfirmClose = () => {
    confirmModal.closeModal();
    setPendingClose(false);
    setIsFormDirty(false);
    setIsSliderOpen(false);
    setSelectedCard(null);
  };

  const handleCancelClose = () => {
    confirmModal.closeModal();
    setPendingClose(false);
  };

  const handleSuccess = (data: unknown) => {
    setErrorMessage(null);
    const formData = data as Record<string, unknown>;
    const buttonId = formData._buttonId as string;
    const isSaveButton = buttonId === 'save';

    const payload = {
      caseParentCategory: selectedCard?.caseParentCategory || '',
      caseSubCategory: selectedCard?.caseSubCategory || '',
      priority: (formData.priority as string) || '',
      caseSubject: (formData.caseSubject as string) || '',
      caseSummary: (formData.caseSummary as string) || '',
      requestedSupport: (formData.requestedSupport as string) || '',
      vin: (formData.vin as string) || '',
      locationOfEvent: (formData.locationOfEvent as string) || '',
      stepsTaken: (formData.stepsTaken as string) || '',
      attachMedia: (formData.attachMedia as string[]) || [],
      status: '',
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      draft: isSaveButton ? 'draft' : '',
    };

    mutate(payload, {
      onSuccess: (response) => {
        // response is already the data object due to LuppApi.extractData
        const caseIdentifier = response?.customerCaseIdentifier;
        const caseIdMessage = formatCaseTitle(caseIdentifier || '-');
        setSuccessMessage(`${SUCCESS_MESSAGE} Case No. - ${caseIdMessage}`);
        scrollSliderToTop();
        setTimeout(() => {
          setIsSliderOpen(false);
          navigate(REDIRECT_PATH);
        }, REDIRECT_DELAY);
      },
      onError: (error: unknown) => {
        setSuccessMessage('');
        const errorMsg = error instanceof Error ? error.message : ERROR_MESSAGE;
        setErrorMessage(errorMsg);
        scrollSliderToTop();
      },
    });
  };

  const handleError = (error: unknown) => {
    console.error('Form validation failed:', error);
    setErrorMessage(VALIDATION_ERROR);
    setSuccessMessage('');
    scrollSliderToTop();
  };

  // If in VIN search mode, show the VinSearch component
  if (vinSearchMode) {
    const sliderTitle =
      vinSearchMode === 'tech-support-non-service' ? 'Tech Support - Non Service' : 'Escalation';
    const formFields =
      vinSearchMode === 'tech-support-non-service' ? techSupportFormFields : escalationFormFields;

    // Wrap static form fields in a function for VinSearch
    const getFormFields = () => formFields;

    return (
      <Box pl={2} pr={2} className={styles.pageContainer}>
        <Box pt={2} mb={16}>
          <Button
            type="tertiary"
            theme={theme}
            handleClick={handleBackToSelection}
            dataTestId="back-to-case-selection"
            gaComponent="create-case"
            label="Back"
            iconLeft={{ category: 'arrows', name: 'chevron-left' }}
          />
        </Box>
        <VinSearch
          theme={theme}
          label="Locate Asset"
          placeholder="Search: VIN / Name / Tel No. / Licence Plate ..."
          emptyMessage="No vehicles found matching your search"
          dataTestId="customer-care-vin-search"
          sliderTitle={sliderTitle}
          getFormFields={getFormFields}
          formTitle="Case Details"
          formId="vin-search-case-form"
          isSubmitting={isPending}
          successMessage={vinSearchSuccessMessage}
          errorMessage={vinSearchErrorMessage}
          onFormSubmit={handleVinSearchFormSubmit}
          onFormError={handleVinSearchFormError}
          onSliderClose={handleVinSearchSliderClose}
        />
      </Box>
    );
  }

  return (
    <Box className={styles.pageContainer}>
      <Typography variant="headline" size="small" color="primary-1" theme={theme}>
        Select Case Type
      </Typography>
      <Box pt={8}>
        <Typography variant="body" size="medium" color="gray-5" theme={theme}>
          Choose a case type to create a new customer care case
        </Typography>
      </Box>

      {caseTypesData.map((section, sectionIndex) => (
        <Box mt={48} key={`section-${sectionIndex}`}>
          <Box mb={16}>
            <Typography variant="body" size="large" color="white" theme={theme}>
              {section.sectionTitle}
            </Typography>
          </Box>
          <Row gutter={[16, 16]} justify="start">
            {section.cards.map((card) => (
              <Col md={3} lg={4} sm={12} key={card.id}>
                <Box
                  className={styles.cardView}
                  background="white"
                  theme={theme}
                  onClick={() => handleCardClick(card)}
                  data-theme={theme}
                >
                  <Typography variant="h3" size="medium" color="primary-1" theme={theme}>
                    {card.title}
                  </Typography>
                  <Typography variant="body" size="small" color="black" theme={theme}>
                    {card.description}
                  </Typography>
                </Box>
              </Col>
            ))}
          </Row>
        </Box>
      ))}

      <Slider
        isOpen={isSliderOpen}
        onClose={handleSliderClose}
        title={selectedCard?.title || 'Create Case'}
        width="1000px"
        theme={invertedTheme}
      >
        <Box ref={sliderContentRef} className={styles.sliderContent}>
          {successMessage && (
            <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.successMessageText}>
              <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
                {successMessage}
              </Typography>
            </Box>
          )}
          {errorMessage && (
            <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.errorMessageText}>
              <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
                {errorMessage}
              </Typography>
            </Box>
          )}
          {isPending && (
            <Box className="fetchingOverlay">
              <Loader size={32} />
            </Box>
          )}
          {selectedCard && (
            <Form
              id="create-case-form"
              theme={invertedTheme}
              formFields={createCaseFormFields}
              onSuccess={handleSuccess}
              onError={handleError}
              onDirtyChange={setIsFormDirty}
              isFluid={true}
            />
          )}
        </Box>
      </Slider>

      <Modal
        isOpen={confirmModal.isOpen && pendingClose}
        onClose={handleCancelClose}
        title="Confirm Close"
        theme={theme}
        size="small"
        dataTestId="close-confirmation-modal"
        footer={
          <Box className={styles.modalFooter}>
            <Button
              label="No"
              type="secondary"
              color="neutral"
              theme={theme}
              handleClick={handleCancelClose}
              gaComponent="close-confirmation-no"
              dataTestId="close-confirmation-no"
            />
            <Button
              label="Yes"
              type="primary"
              color="neutral"
              theme={theme}
              handleClick={handleConfirmClose}
              gaComponent="close-confirmation-yes"
              dataTestId="close-confirmation-yes"
            />
          </Box>
        }
      >
        <Typography variant="body" size="medium" theme={theme} color="white">
          Are you sure you want to close? Any unsaved changes will be lost.
        </Typography>
      </Modal>
    </Box>
  );
};

export default CreateCase;
